import React, { useEffect, useMemo, useState } from 'react'
import {
	Alert,
	Box,
	Button,
	Checkbox,
	CircularProgress,
	Collapse,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControlLabel,
	IconButton,
	InputAdornment,
	MenuItem,
	Paper,
	Select,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material'
import { CheckCircleOutline, Close, Delete, Edit, ExpandLess, ExpandMore, Search, SwapHoriz } from '@mui/icons-material'
import { DeleteConfirmationDialog } from '../DeleteConfirmationDialog'
import { StatusChip } from './StatusChip'

function TransferSensorDialog({ open, sensor, customers, onClose, onConfirm, loading }) {
	const [targetCustomerId, setTargetCustomerId] = useState('')

	useEffect(() => {
		if (open) setTargetCustomerId('')
	}, [open])

	const currentCustomer = customers.find(c => c.id === sensor?.customer_id)

	if (!sensor) return null

	return (
		<Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
			<DialogTitle>
				<Box display="flex" justifyContent="space-between" alignItems="center">
					<Typography variant="h6" fontWeight="bold">Transfer Sensor</Typography>
					<IconButton size="small" onClick={onClose}><Close /></IconButton>
				</Box>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ pt: 3 }}>
				<Alert severity="info" sx={{ mb: 3 }}>
					<Typography variant="body2" fontWeight="bold" gutterBottom>
						Future ownership transfer only
					</Typography>
					<Typography variant="body2">
						New data will be collected under the destination customer. Historical measurements,
						issues, and delivery history remain with <strong>{currentCustomer?.name ?? 'the current customer'}</strong>.
					</Typography>
				</Alert>

				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary" gutterBottom>Sensor</Typography>
					<Typography variant="body1" fontWeight="bold">{sensor.sensor_id}</Typography>
					{sensor.sensor_location && (
						<Typography variant="body2" color="text.secondary">{sensor.sensor_location}</Typography>
					)}
				</Box>

				<Box sx={{ mb: 2 }}>
					<Typography variant="body2" color="text.secondary" gutterBottom>Current Customer</Typography>
					<Typography variant="body1">{currentCustomer?.name ?? '—'}</Typography>
				</Box>

				<Select
					fullWidth
					displayEmpty
					value={targetCustomerId}
					onChange={(e) => setTargetCustomerId(e.target.value)}
					renderValue={(v) => v ? customers.find(c => String(c.id) === String(v))?.name : 'Select destination customer'}
				>
					<MenuItem value="" disabled>Select destination customer</MenuItem>
					{customers
						.filter(c => c.id !== sensor.customer_id)
						.map(c => (
							<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
						))}
				</Select>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ px: 3, py: 2 }}>
				<Button onClick={onClose} variant="outlined">Cancel</Button>
				<Button
					variant="contained"
					startIcon={<SwapHoriz />}
					disabled={!targetCustomerId || loading}
					onClick={() => onConfirm(sensor.id, targetCustomerId)}
				>
					{loading ? 'Transferring…' : 'Transfer Sensor'}
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export function SensorsView({
	customers,
	operationLoading,
	onUpdateSensor,
	onTransferSensor,
	onDeleteSensor,
	onToggleSensorDelivery,
	onToggleSensorRealtimeEnabled,
	onToggleSensorApiEnabled,
	onUpdateSensorVisibility,
}) {
	const [search, setSearch] = useState('')
	const [filterCustomerId, setFilterCustomerId] = useState('')
	const [editingId, setEditingId] = useState(null)
	const [editForm, setEditForm] = useState({ sensor_id: '', sensor_location: '' })
	const [editLoading, setEditLoading] = useState(false)
	const [expandedId, setExpandedId] = useState(null)
	const [transferDialog, setTransferDialog] = useState({ open: false, sensor: null })
	const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null, name: '' })
	const [actionLoading, setActionLoading] = useState(null)

	const allSensors = useMemo(() => {
		return customers.flatMap(c =>
			(c.sensor_configs || []).map(s => ({ ...s, customer_id: c.id, customerName: c.name }))
		)
	}, [customers])

	const filtered = useMemo(() => {
		const q = search.toLowerCase()
		return allSensors.filter(s => {
			const matchesSearch = !q || s.sensor_id.toLowerCase().includes(q) || (s.sensor_location || '').toLowerCase().includes(q) || s.customerName.toLowerCase().includes(q)
			const matchesCustomer = !filterCustomerId || String(s.customer_id) === String(filterCustomerId)
			return matchesSearch && matchesCustomer
		})
	}, [allSensors, search, filterCustomerId])

	const startEdit = (sensor) => {
		setEditingId(sensor.id)
		setEditForm({ sensor_id: sensor.sensor_id, sensor_location: sensor.sensor_location || '' })
	}

	const cancelEdit = () => {
		setEditingId(null)
		setEditForm({ sensor_id: '', sensor_location: '' })
	}

	const handleSaveEdit = async (sensorId) => {
		setEditLoading(true)
		try {
			await onUpdateSensor(sensorId, editForm)
			setEditingId(null)
		} finally {
			setEditLoading(false)
		}
	}

	const handleTransfer = async (sensorConfigId, targetCustomerId) => {
		try {
			setActionLoading(`transfer-${sensorConfigId}`)
			await onTransferSensor(sensorConfigId, targetCustomerId)
			setTransferDialog({ open: false, sensor: null })
		} finally {
			setActionLoading(null)
		}
	}

	const handleDelete = async () => {
		try {
			setActionLoading(`delete-${deleteDialog.id}`)
			await onDeleteSensor(deleteDialog.id)
			setDeleteDialog({ open: false, id: null, name: '' })
		} finally {
			setActionLoading(null)
		}
	}

	return (
		<Box>
			<Box display="flex" gap={2} mb={2.5} flexWrap="wrap" alignItems="center">
				<TextField
					size="small"
					placeholder="Search sensors…"
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					sx={{ width: 280 }}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<Search fontSize="small" />
							</InputAdornment>
						),
					}}
				/>
				<Select
					size="small"
					displayEmpty
					value={filterCustomerId}
					onChange={(e) => setFilterCustomerId(e.target.value)}
					sx={{ minWidth: 200 }}
					renderValue={(v) => v ? customers.find(c => String(c.id) === String(v))?.name : 'All customers'}
				>
					<MenuItem value="">All customers</MenuItem>
					{customers.map(c => (
						<MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
					))}
				</Select>
				<Typography variant="body2" color="text.secondary" sx={{ ml: 'auto' }}>
					{filtered.length} sensor{filtered.length !== 1 ? 's' : ''}
				</Typography>
			</Box>

			<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
				<Table>
					<TableHead>
						<TableRow sx={{ '& th': { fontWeight: 600, color: 'text.secondary', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' } }}>
							<TableCell>Sensor ID</TableCell>
							<TableCell>Location</TableCell>
							<TableCell>Customer</TableCell>
							<TableCell>Delivery</TableCell>
							<TableCell>Realtime</TableCell>
							<TableCell>API</TableCell>
							<TableCell>Metrics</TableCell>
							<TableCell align="right">Actions</TableCell>
						</TableRow>
					</TableHead>
					<TableBody>
						{filtered.length === 0 && (
							<TableRow>
								<TableCell colSpan={8} align="center" sx={{ py: 6 }}>
									<Typography color="text.secondary">
										{search || filterCustomerId ? 'No sensors match your filters.' : 'No sensors configured.'}
									</Typography>
								</TableCell>
							</TableRow>
						)}
						{filtered.map((sensor) => {
							const isEditing = editingId === sensor.id
							const isExpanded = expandedId === sensor.id
							const visibilityFields = [
								{ field: 'show_ph', label: 'pH' },
								{ field: 'show_alkalinity', label: 'Alk' },
								{ field: 'show_temperature', label: 'Temp' },
								{ field: 'show_conductivity', label: 'Cond' },
							]
							return (
								<React.Fragment key={sensor.id}>
									<TableRow hover selected={isEditing}>
										<TableCell>
											{isEditing ? (
												<TextField
													size="small"
													value={editForm.sensor_id}
													onChange={(e) => setEditForm(f => ({ ...f, sensor_id: e.target.value }))}
													sx={{ width: 160 }}
													autoFocus
												/>
											) : (
												<Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace' }}>
													{sensor.sensor_id}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											{isEditing ? (
												<TextField
													size="small"
													value={editForm.sensor_location}
													onChange={(e) => setEditForm(f => ({ ...f, sensor_location: e.target.value }))}
													placeholder="Location"
													sx={{ width: 160 }}
												/>
											) : (
												<Typography variant="body2" color="text.secondary">
													{sensor.sensor_location || '—'}
												</Typography>
											)}
										</TableCell>
										<TableCell>
											<Typography variant="body2">{sensor.customerName}</Typography>
										</TableCell>
										<TableCell>
											<Tooltip title="Toggle delivery">
												<span>
													<StatusChip
														on={sensor.delivery_enabled}
														onLabel="ON"
														offLabel="OFF"
														loading={operationLoading === `sensor-${sensor.customer_id}-${sensor.sensor_id}`}
														onClick={() => onToggleSensorDelivery(sensor.customer_id, sensor.sensor_id)}
													/>
												</span>
											</Tooltip>
										</TableCell>
										<TableCell>
											<Tooltip title="Toggle realtime">
												<span>
													<StatusChip
														on={sensor.realtime_enabled}
														onLabel="ON"
														offLabel="OFF"
														loading={operationLoading === `realtime-${sensor.id}`}
														onClick={() => onToggleSensorRealtimeEnabled(sensor.id)}
													/>
												</span>
											</Tooltip>
										</TableCell>
										<TableCell>
											<Tooltip title="Toggle API access">
												<span>
													<StatusChip
														on={sensor.api_fetch_enabled}
														onLabel="ON"
														offLabel="OFF"
														loading={operationLoading === `api-${sensor.id}`}
														onClick={() => onToggleSensorApiEnabled(sensor.id)}
													/>
												</span>
											</Tooltip>
										</TableCell>
										<TableCell>
											<Tooltip title={isExpanded ? 'Hide metric controls' : 'Show metric controls'}>
												<IconButton
													size="small"
													onClick={() => setExpandedId(isExpanded ? null : sensor.id)}
												>
													{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
												</IconButton>
											</Tooltip>
										</TableCell>
										<TableCell align="right">
											{isEditing ? (
												<Box display="flex" gap={0.5} justifyContent="flex-end">
													<Tooltip title="Save">
														<IconButton
															size="small"
															color="primary"
															disabled={editLoading || !editForm.sensor_id.trim()}
															onClick={() => handleSaveEdit(sensor.id)}
														>
															{editLoading ? <CircularProgress size={16} /> : <CheckCircleOutline fontSize="small" />}
														</IconButton>
													</Tooltip>
													<Tooltip title="Cancel">
														<IconButton size="small" onClick={cancelEdit}>
															<Close fontSize="small" />
														</IconButton>
													</Tooltip>
												</Box>
											) : (
												<Box display="flex" gap={0.5} justifyContent="flex-end">
													<Tooltip title="Edit sensor">
														<IconButton size="small" onClick={() => startEdit(sensor)}>
															<Edit fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Transfer to another customer">
														<IconButton size="small" onClick={() => setTransferDialog({ open: true, sensor })}>
															<SwapHoriz fontSize="small" />
														</IconButton>
													</Tooltip>
													<Tooltip title="Delete sensor">
														<IconButton
															size="small"
															color="error"
															onClick={() => setDeleteDialog({ open: true, id: sensor.id, name: sensor.sensor_id })}
														>
															<Delete fontSize="small" />
														</IconButton>
													</Tooltip>
												</Box>
											)}
										</TableCell>
									</TableRow>
									<TableRow>
										<TableCell colSpan={8} sx={{ py: 0, border: isExpanded ? undefined : 'none' }}>
											<Collapse in={isExpanded} unmountOnExit>
												<Box sx={{ px: 2, py: 1.5, bgcolor: 'action.hover', display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
													<Typography variant="caption" color="text.secondary" sx={{ mr: 1, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
														Visible metrics:
													</Typography>
													{visibilityFields.map(({ field, label }) => (
														<FormControlLabel
															key={field}
															control={
																<Checkbox
																	size="small"
																	checked={!!sensor[field]}
																	onChange={(e) => onUpdateSensorVisibility(sensor.id, {
																		show_ph: field === 'show_ph' ? e.target.checked : !!sensor.show_ph,
																		show_alkalinity: field === 'show_alkalinity' ? e.target.checked : !!sensor.show_alkalinity,
																		show_conductivity: field === 'show_conductivity' ? e.target.checked : !!sensor.show_conductivity,
																		show_temperature: field === 'show_temperature' ? e.target.checked : !!sensor.show_temperature,
																	})}
																/>
															}
															label={<Typography variant="caption">{label}</Typography>}
															sx={{ mr: 0.5 }}
														/>
													))}
												</Box>
											</Collapse>
										</TableCell>
									</TableRow>
								</React.Fragment>
							)
						})}
					</TableBody>
				</Table>
			</TableContainer>

			<TransferSensorDialog
				open={transferDialog.open}
				sensor={transferDialog.sensor}
				customers={customers}
				onClose={() => setTransferDialog({ open: false, sensor: null })}
				onConfirm={handleTransfer}
				loading={!!actionLoading?.startsWith('transfer-')}
			/>

			<DeleteConfirmationDialog
				open={deleteDialog.open}
				onClose={() => setDeleteDialog({ open: false, id: null, name: '' })}
				onConfirm={handleDelete}
				title="Delete Sensor"
				itemName={deleteDialog.name}
				itemType="sensor"
			/>
		</Box>
	)
}
